/**
 * @studio/design-tokens
 *
 * TypeScript constants for JS-driven values referenced at runtime
 * (ClockBar thresholds, motion durations, semantic color names).
 *
 * Source: app/docs/aldente/design-guidelines.md (DES-04, §8 Motion).
 */

export const TOKENS_VERSION = '0.3.0';

/**
 * ClockBar thresholds, per tier, in hours.
 *
 * Cycle endpoints: Studio 72h, Pro 48h. Color transitions happen at the
 * thresholds defined below. PR-10 / DES-04.
 */
export const CLOCK_BAR_THRESHOLDS = {
  studio: {
    cycleHours: 72,
    greenUntilHours: 48,
    amberUntilHours: 60,
    // red: 60 -> 72
  },
  pro: {
    cycleHours: 48,
    greenUntilHours: 32,
    amberUntilHours: 40,
    // red: 40 -> 48
  },
} as const;

export type Tier = keyof typeof CLOCK_BAR_THRESHOLDS;

/**
 * Motion durations in milliseconds. Mirrors --motion-duration-* CSS vars.
 */
export const MOTION_DURATIONS_MS = {
  fast: 150,
  base: 200,
  slow: 250,
  slower: 300,
  clockPulse: 1400,
  toastDismiss: 4000,
} as const;

/**
 * Semantic color names — convenience re-exports for JS code that needs to
 * pick a color by intent rather than read the CSS variable directly. The
 * actual color values are owned by tokens.css.
 */
export const CSS_VAR = {
  navy: 'var(--color-navy)',
  navyHover: 'var(--color-navy-hover)',
  navyDeep: 'var(--color-navy-deep)',
  rose: 'var(--color-rose)',
  roseSoft: 'var(--color-rose-soft)',
  roseDeep: 'var(--color-rose-deep)',
  roseSubtle: 'var(--color-rose-subtle)',
  white: 'var(--color-white)',
  linen: 'var(--color-linen)',
  linenDeep: 'var(--color-linen-deep)',
  black: 'var(--color-black)',
  gray100: 'var(--color-gray-100)',
  gray300: 'var(--color-gray-300)',
  gray500: 'var(--color-gray-500)',
  gray700: 'var(--color-gray-700)',
  textSoft: 'var(--color-text-soft)',
  textMute: 'var(--color-text-mute)',
  rule: 'var(--color-rule)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  error: 'var(--color-error)',
  info: 'var(--color-info)',

  // App-only
  terracotta: 'var(--color-terracotta)',
  clockGreen: 'var(--color-clock-green)',
  clockAmber: 'var(--color-clock-amber)',
  clockRed: 'var(--color-clock-red)',
} as const;

export const tokens = {
  version: TOKENS_VERSION,
  clockBar: CLOCK_BAR_THRESHOLDS,
  motion: MOTION_DURATIONS_MS,
  cssVar: CSS_VAR,
} as const;

export type Tokens = typeof tokens;
